import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Spinner,
  Badge,
  Collapse
} from "reactstrap";
import { enqueueSnackbar } from "notistack";
import {
  getCombineHistory,
  getCombineHistoryBatch,
  revertCombineHistoryBatch
} from "../../../../services/ValitanApiService";

const normalizeText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getHistoryAssetDisplayName = (item) => {
  const name = normalizeText(item?.assetName || item?.asset_name);
  const otherName = normalizeText(item?.otherName || item?.other_name);
  if ((name.toUpperCase() === "OTHERS" || name.toUpperCase() === "OTHER") && otherName) {
    return otherName;
  }
  return name || otherName || "-";
};

const CombineHistoryModal = ({ isOpen, toggle }) => {
  const [loading, setLoading] = useState(false);
  const [historyGroups, setHistoryGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loadingBatchId, setLoadingBatchId] = useState(null);
  const [revertingBatchId, setRevertingBatchId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getCombineHistory();
      const data = response?.data?.data || response?.data || [];
      setHistoryGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch combine history:", error);
      enqueueSnackbar("Failed to fetch combine history.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getBatchId = (group) => group.batchId || group.id;

  const toggleGroup = (batchId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  const upsertGroup = (batchId, nextGroup) => {
    setHistoryGroups((previous) =>
      previous.map((group) => (getBatchId(group) === batchId ? nextGroup : group))
    );
  };

  const handleViewDetails = async (group) => {
    const batchId = getBatchId(group);
    if (!batchId) return;
    setLoadingBatchId(batchId);
    try {
      const response = await getCombineHistoryBatch(batchId);
      const details = response?.data?.data || response?.data;
      if (details) {
        upsertGroup(batchId, details);
      }
      setExpandedGroups((previous) => ({ ...previous, [batchId]: true }));
    } catch (error) {
      console.error("Failed to fetch combine details:", error);
      enqueueSnackbar(error?.response?.data?.message || "Failed to fetch combine details.", { variant: "error" });
    } finally {
      setLoadingBatchId(null);
    }
  };

  const handleRevert = async (group) => {
    const batchId = getBatchId(group);
    if (!batchId || group.reverted) return;
    const confirmed = window.confirm("Revert this combine batch back to individual assets?");
    if (!confirmed) return;

    setRevertingBatchId(batchId);
    try {
      const response = await revertCombineHistoryBatch(batchId);
      enqueueSnackbar(response?.data?.message || "Combine batch reverted.", { variant: "success" });
      await fetchHistory();
    } catch (error) {
      console.error("Failed to revert combine batch:", error);
      enqueueSnackbar(error?.response?.data?.message || "Failed to revert combine batch.", { variant: "error" });
    } finally {
      setRevertingBatchId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? dateString : d.toLocaleString();
  };

  const getGroupBuildingTotals = (group) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    return items.reduce((acc, item) => ({
      buildings: acc.buildings + (Number(item?.numberOfBuildings) || 0),
      sites: acc.sites + (Number(item?.siteWorksCount) || 0)
    }), { buildings: 0, sites: 0 });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        <i className="ri-history-line me-2 text-primary"></i>
        Combine Institutions Log
      </ModalHeader>
      <ModalBody className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading combine history...</p>
          </div>
        ) : historyGroups.length === 0 ? (
          <div className="text-center py-5 text-muted">
            No combination history found.
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Date / Time</th>
                  <th>Target Institution</th>
                  <th>Assets Combined</th>
                  <th>Qty Trend</th>
                  <th>Status</th>
                  <th>Performed By</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyGroups.map((group) => (
                  <React.Fragment key={getBatchId(group)}>
                    <tr>
                      <td className="text-center">
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 text-body"
                          onClick={() => toggleGroup(getBatchId(group))}
                          title={expandedGroups[getBatchId(group)] ? "Hide details" : "Show details"}
                        >
                          <i className={`ri-arrow-${expandedGroups[getBatchId(group)] ? 'down' : 'right'}-s-line fs-18`}></i>
                        </Button>
                      </td>
                      <td>{formatDate(group.performedAt)}</td>
                      <td className="fw-medium text-primary">{group.targetInstitutionName}</td>
                      <td><Badge color="info">{group.assetsCount} Assets</Badge></td>
                      <td>
                        <Badge color={group.quantityMatchesAssetsCount === false ? "warning" : "success"}>
                          Qty {group.totalQuantity ?? "-"}
                        </Badge>
                      </td>
                      <td>
                        {group.reverted ? (
                          <Badge color="secondary">Reverted</Badge>
                        ) : (
                          <Badge color="success">Active</Badge>
                        )}
                      </td>
                      <td>{group.performedByUsername || "-"}</td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => handleViewDetails(group)}
                            disabled={loadingBatchId === getBatchId(group)}
                          >
                            {loadingBatchId === getBatchId(group) ? (
                              <Spinner size="sm" />
                            ) : (
                              <>
                                <i className="ri-eye-line me-1"></i>
                                Details
                              </>
                            )}
                          </Button>
                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => handleRevert(group)}
                            disabled={group.reverted || revertingBatchId === getBatchId(group)}
                          >
                            {revertingBatchId === getBatchId(group) ? (
                              <Spinner size="sm" />
                            ) : (
                              <>
                                <i className="ri-arrow-go-back-line me-1"></i>
                                Revert
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="8" className="p-0 border-0">
                        <Collapse isOpen={expandedGroups[getBatchId(group)]}>
                          <div className="p-3 bg-light border-bottom">
                            {(() => {
                              const hasBuildingFields = (group.items || []).some(
                                (item) => item?.numberOfBuildings != null || item?.siteWorksCount != null
                              );
                              const emptyColSpan = hasBuildingFields ? 7 : 5;
                              return (
                                <>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                              <h6 className="mb-0 fs-13 text-muted text-uppercase">Combined Assets Details</h6>
                              <div className="d-flex align-items-center gap-2">
                                <Badge color={group.quantityMatchesAssetsCount === false ? "warning" : "success"}>
                                  Assets {group.assetsCount ?? 0} / Qty {group.totalQuantity ?? "-"}
                                </Badge>
                                {(() => {
                                  const totals = getGroupBuildingTotals(group);
                                  if (!totals.buildings && !totals.sites) return null;
                                  return (
                                    <Badge color="info">
                                      Buildings {totals.buildings || 0} / Sites {totals.sites || 0}
                                    </Badge>
                                  );
                                })()}
                                {group.revertedAt && (
                                  <span className="text-muted fs-12">
                                    Reverted {formatDate(group.revertedAt)} by {group.revertedByUsername || "-"}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Table size="sm" className="mb-0 bg-white shadow-sm rounded border">
                              <thead className="table-light">
                                <tr>
                                  <th>Asset Name</th>
                                  <th>Description</th>
                                  <th>Quantity</th>
                                  {hasBuildingFields && <th>Buildings</th>}
                                  {hasBuildingFields && <th>Sites</th>}
                                  <th>Old Barcode</th>
                                  <th>Old Institution</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(group.items || []).map((item, idx) => (
                                    <tr key={item.assetId || idx}>
                                      <td className="fw-medium">{getHistoryAssetDisplayName(item)}</td>
                                      <td className="text-muted text-truncate" style={{ maxWidth: "200px" }}>{item.assetDescription || "-"}</td>
                                      <td>{item.quantity ?? "-"}</td>
                                      {hasBuildingFields && <td>{item.numberOfBuildings ?? "-"}</td>}
                                      {hasBuildingFields && <td>{item.siteWorksCount ?? "-"}</td>}
                                      <td>{item.oldBarcode || "-"}</td>
                                      <td>{item.oldInstitutionName || "-"}</td>
                                    </tr>
                                  ))}
                                {(!group.items || group.items.length === 0) && (
                                  <tr>
                                    <td colSpan={emptyColSpan} className="text-center py-3 text-muted">
                                      No detailed items found for this combination.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                                </>
                              );
                            })()}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CombineHistoryModal;
